
import { Layers, LayoutGrid, List, Trash2, RefreshCw } from 'lucide-react'
import { TxtIcon } from '../Icons/TxtIcon'
import type { RAGDocument } from '@/types/rag'
import { KnowledgeGrid } from '../Tabs/Grid/KnowledgeGrid'
import { DocumentCard } from '../../Shared/DocumentCard'

interface DocumentsSectionProps {
    documents: RAGDocument[]
    selectedDocs: Set<string>
    viewMode: 'grid' | 'list'
    isLoading: boolean
    isBulkDeleting: boolean
    isMobile: boolean
    expandedRows: Set<string>
    onViewModeChange: (mode: 'grid' | 'list') => void
    onToggleSelect: (id: string, selected: boolean) => void
    onView: (doc: RAGDocument) => void
    onDelete: (id: string) => void
    onBulkDelete: () => void
    onToggleRow: (id: string) => void
    onToggleSelectAll: () => void
    totalCount: number
}

export function DocumentsSection({
    documents,
    selectedDocs,
    viewMode,
    isLoading,
    isBulkDeleting,
    isMobile,
    expandedRows,
    onViewModeChange,
    onToggleSelect,
    onView,
    onDelete,
    onBulkDelete,
    onToggleRow,
    onToggleSelectAll,
    totalCount
}: DocumentsSectionProps) {
    return (
        <section className="h-full flex flex-col px-6 py-6 overflow-hidden">
            {/* Header - Fixed */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Layers className="w-5 h-5 text-emerald-400" />
                    Documentos
                </h3>
                <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 font-medium">Step 2 of 3</span>

                    {/* Select All / Total */}
                    <button
                        onClick={onToggleSelectAll}
                        className="text-xs font-medium text-gray-400 hover:text-white transition-colors px-2 py-1 hover:bg-white/5 rounded"
                    >
                        {selectedDocs.size === totalCount && totalCount > 0 ? 'Deselect All' : 'Select All'}
                    </button>
                    <div className="text-xs text-gray-500 font-mono">
                        Total: {totalCount}
                    </div>

                    {/* View Toggle */}
                    <div className="flex gap-1 bg-white/5 border border-white/10 rounded-lg p-1">
                        <button
                            onClick={() => onViewModeChange('grid')}
                            className={`px-3 py-1.5 rounded text-sm transition-all ${viewMode === 'grid'
                                ? 'bg-emerald-500 text-white'
                                : 'text-gray-400 hover:text-white'
                                }`}
                            title="Grid View"
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => onViewModeChange('list')}
                            className={`px-3 py-1.5 rounded text-sm transition-all ${viewMode === 'list'
                                ? 'bg-emerald-500 text-white'
                                : 'text-gray-400 hover:text-white'
                                }`}
                            title="List View"
                        >
                            <List className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Bulk Actions Bar (if selection exists) - Fixed */}
            {selectedDocs.size > 0 && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 mb-4 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <span className="text-emerald-300 text-sm font-medium">
                            {selectedDocs.size} selected
                        </span>
                        <button
                            onClick={onBulkDelete}
                            disabled={isBulkDeleting}
                            className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm font-medium disabled:opacity-50 transition-colors"
                        >
                            {isBulkDeleting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            Delete Selected
                        </button>
                    </div>
                </div>
            )}

            {/* SCROLLABLE Documents Area - Independent Scroll */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden pr-2 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent min-h-0">
                {isLoading && documents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <RefreshCw className="w-8 h-8 mb-4 animate-spin opacity-50" />
                        <p className="text-sm">Loading documents...</p>
                    </div>
                ) : documents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <TxtIcon className="w-12 h-12 mb-4 opacity-50" />
                        <p className="text-sm">No documents yet</p>
                        <p className="text-xs text-gray-600 mt-1">Upload files to get started</p>
                    </div>
                ) : viewMode === 'grid' ? (
                    /* Preserve existing KnowledgeGrid with all responsive features */
                    <KnowledgeGrid
                        documents={documents}
                        selectedIds={selectedDocs}
                        onToggleSelect={onToggleSelect}
                        onView={onView}
                        onDelete={onDelete}
                        isLoading={isLoading}
                    />
                ) : isMobile ? (
                    /* Preserve existing List view with responsive DocumentCard for mobile */
                    <div className="space-y-3">
                        {documents.map(doc => (
                            <DocumentCard
                                key={doc.id}
                                doc={doc}
                                isSelected={selectedDocs.has(doc.id)}
                                isExpanded={expandedRows.has(doc.id)}
                                onToggleSelect={() => onToggleSelect(doc.id, !selectedDocs.has(doc.id))}
                                onExpand={() => onToggleRow(doc.id)}
                                onDelete={(id) => onDelete(id)}
                            />
                        ))}
                    </div>
                ) : (
                    /* Desktop list view - simplified for now, can be enhanced later */
                    <div className="space-y-2">
                        {documents.map(doc => (
                            <div
                                key={doc.id}
                                className="bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/10 transition-colors cursor-pointer"
                                onClick={() => onView(doc)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3 flex-1">
                                        <input
                                            type="checkbox"
                                            checked={selectedDocs.has(doc.id)}
                                            onChange={(e) => {
                                                e.stopPropagation()
                                                onToggleSelect(doc.id, e.target.checked)
                                            }}
                                            className="rounded bg-white/10 border-white/20"
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <TxtIcon className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-200">{doc.filename}</span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-xs text-gray-500">{doc.chunks_count || 0} chunks</span>
                                        <span className={`text-xs px-2 py-1 rounded ${doc.status === 'INDEXED' ? 'bg-green-500/20 text-green-400' :
                                            doc.status === 'PROCESSING' ? 'bg-blue-500/20 text-blue-400' :
                                                'bg-gray-500/20 text-gray-400'
                                            }`}>
                                            {doc.status}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    )
}
